import React, { useState, useRef, useEffect, useMemo } from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { useForm } from 'react-hook-form';
import _ from 'lodash';
import moment from 'moment';
import PropTypes from 'prop-types';

import Button from '../../../../components/Button';
import { nextAction } from '../../../../saga/actions/workflow';
import { getNextUrl } from '../url';
import AgreeCheckboxes from './Step7/AgreeCheckboxes';
import ImportantInformation from './Step7/ImportantInformation';
import RevisionForm from './Step7/RevisionForm';
import { unmaskCurrency, unmaskPhoneNumber } from '../../../../utils/masks';
import { getHTTPErrorMessage } from '../../../../utils/fetch';

const filterDefaultData = (data) => {
  const filteredData = { ...data };
  filteredData.dateOfBirth = moment(data.dateOfBirth).format('MM/DD/YYYY');
  const last4 = data.ssn;
  filteredData.SSN1 = '***';
  filteredData.SSN2 = '**';
  filteredData.SSN3 = last4;
  filteredData.ssn = `*****${last4}`;
  return filteredData;
};

const filterSubmitData = (submitData, defaultData) => {
  const ret = {};
  _.map(submitData, (value, key) => {
    switch (key) {
      case 'dateOfBirth':
        if (moment(value).format('YYYY-MM-DD') !== moment(defaultData[key]).format('YYYY-MM-DD')) {
          ret[key] = value;
        }
        break;
      default:
        if (value !== defaultData[key]) {
          ret[key] = value;
        }
        break;
    }
  });
  return ret;
};

const scrollToErrors = (errorNames) => {
  const elements = errorNames.map((name) => document.getElementsByName(name)[0]).filter((el) => !!el);
  elements.sort((a, b) => b.scrollHeight - a.scrollHeight);
  elements[0]?.scrollIntoView({ behavior: 'smooth', block: 'center' });
};

const Step7 = (props) => {
  const childRef = useRef(null);
  // get functions to build form with useForm() hook
  const data = _.get(props.workflow, 'data.data') || {};
  const isRevisionFormVisible = !_.isEmpty(data);
  const defaultValues = isRevisionFormVisible ? filterDefaultData(data) : {};

  const hookForm = useForm({
    reValidateMode: 'onSubmit',
    defaultValues,
  });

  const { handleSubmit, setError } = hookForm;
  const [isFormVisible] = useState(isRevisionFormVisible);
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [isElectronicChecked, setIsElectronicChecked] = useState(false);
  const [isConsentChecked, setIsConsentChecked] = useState(false);
  const errorMsg = useMemo(() => getHTTPErrorMessage(props.workflow.error), [props.workflow]);
  const workflowError = props.workflow.error;

  useEffect(() => {
    if (workflowError) {
      const errorObj = _.get(workflowError, 'data.failure.errors.0');
      if (errorObj) {
        console.log('error from api', errorObj);
        setError(errorObj.subject, { message: errorObj.message });
        scrollToErrors([errorObj.subject]);
      }
    }
  }, [workflowError, setError]);

  // handle functions
  const onSubmit = (formData) => {
    let retData = {};
    if (isRevisionFormVisible) {
      if (childRef.current.needVerifySmartyAddress()) {
        childRef.current.verifySmartyAddress();
        return false;
      }
      const submitData = {
        requestedAmount: unmaskCurrency(formData.requestedAmount),
        email: formData.email,
        phoneNumber: unmaskPhoneNumber(formData.phoneNumber),
        firstName: formData.firstName,
        lastName: formData.lastName,
        streetAddress: formData.streetAddress,
        city: formData.city,
        state: formData.state,
        zipCode: formData.zipCode,
        ssn: `${formData.SSN1}${formData.SSN2}${formData.SSN3}`,
        dateOfBirth: moment(data.dateOfBirth, 'MM/DD/YYYY').format('YYYY-MM-DD'),
        grossMonthlyIncome: unmaskCurrency(formData.grossMonthlyIncome),
        employmentStatus: formData.employmentStatus,
        employerName: formData.employerName.trim(),
      };
      retData = filterSubmitData(submitData, defaultValues);
    }

    props.nextAction({
      url: getNextUrl(),
      method: 'PUT',
      data: retData,
    });

    return false;
  };

  const onFailure = (errors) => {
    scrollToErrors(Object.keys(errors));
  };

  const handleConsentChange = (checked) => {
    setIsConsentChecked(checked);
  };

  const handleElectronicChange = (checked) => {
    setIsElectronicChecked(checked);
  };

  const handleImportantInformation = (checked) => {
    setIsPopoverOpen(checked);
  };

  return (
    <form onSubmit={handleSubmit(onSubmit, onFailure)}>
      <div className="text-center md:text-left font-bold text-[26px] leading-[26px] md:text-[32px] md:leading-[38px] text-[#263952]">
        Great work. You&apos;re almost done!
        {' '}
        <br />
        {' '}
        Submit your application.
      </div>
      <div className="text-center md:text-left font-normal text-[14px] leading-[20px] text-[#9B9B9B] mt-[5px] ">
        This won&apos;t affect your credit score
        {' '}
        <sup>1</sup>
      </div>
      {isFormVisible
        && (
          <RevisionForm
            ref={childRef}
            hookForm={hookForm}
            defaultValues={defaultValues}
          />
        )}
      <AgreeCheckboxes
        handleElectronicChange={handleElectronicChange}
        handleConsentChange={handleConsentChange}
      />
      <ImportantInformation
        isPopoverOpen={isPopoverOpen}
        handleImportantInformation={handleImportantInformation}
      />
      <Button
        type="submit"
        isLoading={props.workflow.isLoading}
        isDisabled={!isElectronicChecked || !isConsentChecked}
        className="w-full md:w-[240px]"
      >
        SUBMIT NOW
      </Button>
      {errorMsg && <div className="mt-3 mb-3 text-[#d71f28] text-xs">{errorMsg}</div>}
    </form>
  );
};

Step7.propTypes = {
  workflow: PropTypes.object.isRequired,
  nextAction: PropTypes.func.isRequired,
};

export default compose(
  connect(
    (state) => ({
      workflow: state.workflow,
    }),
    {
      nextAction,
    }
  )
)(Step7);
